import React from 'react';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FormikProvider, useFormik } from 'formik';
import { caseApplicantWithNonMandatoryOibValidationSchema } from 'app/edozvola/cases/case-applicant/model/case-applicant.schema';
import { caseApplicantInitialData } from 'app/edozvola/cases/case-applicant/model/case-applicant.model';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import OibInput from 'app/shared/oib/oib-input';
import AddressInput from 'app/shared/address-input/address-input';
import { LegalEntityModel, PersonModel } from 'app/shared/oib/oib.model';
import { APPLICANT_PARTY_TYPE_ENUM, applicantPartyTypes, SUBJECT_TYPE_ENUM } from 'app/shared/enumeration/enums';
import { Button, Input, Modal, Toggle } from 'edozvola-frontend-lib/dist/ui/components';
import { useParams } from 'react-router-dom';
import {
  fetchApplicantsForAssigneeQuery,
  fetchCaseApplicantQuery,
  saveApplicantQuery,
} from 'app/edozvola/cases/case-applicant/service/case-applicant.queries';
import { Loader } from 'app/shared/loader/loader';
import { queryClient } from 'app/app';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { formatAddress } from 'app/shared/address-input/address-helper';
import { fetchProcurationTypesQuery, fetchSubjectTypeQuery } from 'app/edozvola/codebooks/codebooks.queries';
import {ToggleWithLabels} from "edozvola-frontend-lib/dist/ui/components/toggle-with-labels";

export interface ICaseApplicantFormProps {
  caseApplicantId: string | null;
  setCaseApplicantId: any;
  readOnly: boolean;
  systemRecorded: boolean;
}

const CaseApplicantFormWithNonMandatoryOib = (props: ICaseApplicantFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const { data: procurationTypeOptions, isLoading: procurationTypeOptionsLoading } = fetchProcurationTypesQuery();
  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeQuery();

  const { data: applicantData, isLoading: applicantDataLoading } = fetchCaseApplicantQuery(props.caseApplicantId);

  const formik = useFormik({
    initialValues: {
      ...(applicantData ?? caseApplicantInitialData),
      earhiva: true,
    },
    onSubmit: values => saveApplicantMutation(values),
    validationSchema: caseApplicantWithNonMandatoryOibValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    initialTouched: {
      person: {
        oib: !applicantData,
      },
      legalEntity: {
        oib: !applicantData,
      },
      applicantAddress: {
        cityMunicipality: true,
        community: true,
        postalCode: true,
        street: true,
      },
    },
  });

  const { mutate: saveApplicantMutation, isPending: isApplicantSaving } = saveApplicantQuery(uuid, () => closeModal());

  const filteredSubjectTypeOptions = React.useMemo(() => {
    if (!formik.values.partyType || formik.values.partyType[0].id === APPLICANT_PARTY_TYPE_ENUM.PODNOSITELJ) {
      return subjectTypeOptions;
    }

    // Ako je opunomoćenik => isključi obrt (3) i OPG (5)
    if (formik.values.partyType[0].id === APPLICANT_PARTY_TYPE_ENUM.OPUNOMOCENIK_PODNOSITELJA) {
      return subjectTypeOptions?.filter((opt: any) => opt.id !== SUBJECT_TYPE_ENUM.OBRT && opt.id !== SUBJECT_TYPE_ENUM.OPG);
    }

    return subjectTypeOptions;
  }, [formik.values.partyType, subjectTypeOptions]);

  const isOsobaSelectedEqual = (subjectType: SUBJECT_TYPE_ENUM) =>
    formik.values.subjectType && formik.values.subjectType[0] && formik.values.subjectType[0].id === subjectType;

  const isPravnaIliFizickaOsobaSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA);
  const isObrtSlobodnaDjelatnostIliOpgSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT) ||
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.SLOBODNA_DJELATNOST) ||
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OPG);

  const isPartySelectedEqual = (partyType: APPLICANT_PARTY_TYPE_ENUM) =>
    formik.values.partyType && formik.values.partyType[0] && formik.values.partyType[0].id === partyType;

  const onOibFetched = (fetchedOibData: PersonModel | LegalEntityModel) => {
    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA)) {
      formik.setFieldValue('person', fetchedOibData);
    } else {
      formik.setFieldValue('legalEntity', fetchedOibData);
    }
  };

  const onOibChanged = (value: string) => {
    formik.setFieldValue('person.address', null);
    formik.setFieldValue('person.firstName', '');
    formik.setFieldValue('person.lastName', '');
    formik.setFieldValue('legalEntity.companyName', '');
    formik.setFieldValue('legalEntity.address', null);

    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)) {
      formik.setTouched({
        ...formik.touched,
        legalEntity: {
          oib: true,
        },
      });
      formik.setFieldValue('legalEntity.oib', value);
    } else {
      formik.setTouched({
        ...formik.touched,
        person: {
          oib: true,
        },
      });

      formik.setFieldValue('person.oib', value);
    }
  };

  const onSubjectTypeChange = selected => {
    onOibChanged('');
    formik.setFieldValue('legalEntity.mb', '');
    formik.setFieldValue('subjectType', selected);

    const newSubjectTypeId = selected?.[0]?.id;

    if (
      newSubjectTypeId === SUBJECT_TYPE_ENUM.OBRT ||
      newSubjectTypeId === SUBJECT_TYPE_ENUM.SLOBODNA_DJELATNOST ||
      newSubjectTypeId === SUBJECT_TYPE_ENUM.OPG
    ) {
      formik.setFieldValue('oibFetched', false);
    } else {
      formik.setFieldValue('oibFetched', true);
    }

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const { data: applicantsOptions, isLoading: applicantsOptionsLoading } = fetchApplicantsForAssigneeQuery(
    uuid,
    isPartySelectedEqual(APPLICANT_PARTY_TYPE_ENUM.OPUNOMOCENIK_PODNOSITELJA),
    props.caseApplicantId
  );

  const closeModal = () => {
    props.setCaseApplicantId(null);
    queryClient.invalidateQueries({ queryKey: ['case-applicant'] });
    formik.resetForm({ values: caseApplicantInitialData });
  };

  const phoneChanged = (event: any, field: string) => {
    formik.setFieldValue(field, event.target.value);
    event.preventDefault();
  };

  const oibFetchedChanged = (value: boolean) => {
    formik.setFieldValue('person.firstName', '');
    formik.setFieldValue('person.lastName', '');
    formik.setFieldValue('person.oib', '');
    formik.setFieldValue('person.address', null);
    formik.setFieldValue('legalEntity.companyName', '');
    formik.setFieldValue('legalEntity.oib', '');
    formik.setFieldValue('legalEntity.address', null);
    formik.setFieldValue('oibFetched', value).then(() => {
      formik.validateForm();
    });
  };

  const onApplicantPartyTypeChange = (selected: any) => {
    formik.setFieldValue('partyType', selected);
    onOibChanged('');
    formik.setFieldValue('subjectType', []);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  return (
    <>
      <Modal size={'xl'} isOpen={props.caseApplicantId !== null}>
        {props.readOnly ? (
          <ModalHeader toggle={() => closeModal()}>Pregled podnositelja/opunomoćenika</ModalHeader>
        ) : (
          <ModalHeader>
            {props.caseApplicantId === '' ? 'Unos podnositelja/opunomoćenika' : 'Uređivanje podnositelja/opunomoćenika'}
          </ModalHeader>
        )}
        <ModalBody>
          <FormikProvider value={formik}>
            {applicantDataLoading && <Loader />}
            {!applicantDataLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta stranke'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu stranke'}
                      onItemSelect={selected => onApplicantPartyTypeChange(selected)}
                      selected={formik.values.partyType}
                      labelKey={'value'}
                      options={applicantPartyTypes}
                      disabled={props.readOnly || props.caseApplicantId !== ''}
                      error={(formik.errors.partyType as string) ?? ''}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta subjekta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu subjekta'}
                      onItemSelect={onSubjectTypeChange}
                      selected={formik.values.subjectType}
                      labelKey={'value'}
                      options={filteredSubjectTypeOptions}
                      isLoading={subjectTypeOptionsLoading}
                      disabled={props.readOnly || props.caseApplicantId !== ''}
                      error={(formik.errors.subjectType as string) ?? ''}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={4}>
                    {isPravnaIliFizickaOsobaSelected ? (
                      <OibInput
                        value={
                          isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.values.legalEntity?.oib : formik.values.person?.oib
                        }
                        handleBlur={formik.handleBlur}
                        handleChange={event => onOibChanged(event.target.value)}
                        errors={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.errors.legalEntity : formik.errors.person}
                        touched={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.touched.legalEntity : formik.touched.person}
                        onOibFetched={onOibFetched}
                        fetchLegalEntity={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                        disabled={
                          props.readOnly ||
                          !formik.values.subjectType ||
                          formik.values.subjectType.length === 0 ||
                          !formik.values.oibFetched ||
                          props.caseApplicantId !== ''
                        }
                      />
                    ) : (
                      isObrtSlobodnaDjelatnostIliOpgSelected && (
                        <>
                          <Label labelText="MB" />
                          <Input
                            value={formik.values.legalEntity?.mb}
                            id="mb"
                            name="mb"
                            maxLength={11}
                            disabled={props.readOnly || props.caseApplicantId !== ''}
                            onChange={event => formik.setFieldValue('legalEntity.mb', event.target.value)}
                            error={formik.errors.legalEntity && formik.errors.legalEntity['mb'] ? formik.errors.legalEntity['mb'] : ''}
                          />
                        </>
                      )
                    )}
                  </Col>
                  {isPravnaIliFizickaOsobaSelected && (
                    <Col md={2}>
                      <Label labelText={'OIB nepoznat'} />
                      <Toggle
                        disabled={props.readOnly}
                        checked={!formik.values.oibFetched}
                        value={!formik.values.oibFetched as any}
                        onChange={event => oibFetchedChanged(!event.target.checked)}
                      />
                    </Col>
                  )}
                </Row>
                {isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Ime'} />
                      <Input
                        disabled={props.readOnly || formik.values.oibFetched}
                        value={formik.values.person.firstName}
                        error={formik.errors.person && formik.errors.person['firstName'] ? formik.errors.person['firstName'] : ''}
                        maxLength={200}
                        onChange={event => formik.setFieldValue('person.firstName', event.target.value)}
                      />
                    </Col>
                    <Col md={6}>
                      <Label labelText={'Prezime'} />
                      <Input
                        disabled={props.readOnly || formik.values.oibFetched}
                        value={formik.values.person.lastName}
                        error={formik.errors.person && formik.errors.person['lastName'] ? formik.errors.person['lastName'] : ''}
                        maxLength={200}
                        onChange={event => formik.setFieldValue('person.lastName', event.target.value)}
                      />
                    </Col>
                  </Row>
                )}
                {(isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isObrtSlobodnaDjelatnostIliOpgSelected) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Naziv'} />
                      <Input
                        disabled={props.readOnly || (formik.values.oibFetched && isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA))}
                        value={formik.values.legalEntity.companyName}
                        error={
                          formik.errors.legalEntity && formik.errors.legalEntity['companyName']
                            ? formik.errors.legalEntity['companyName']
                            : ''
                        }
                        maxLength={200}
                        onChange={event => formik.setFieldValue('legalEntity.companyName', event.target.value)}
                      />
                    </Col>
                  </Row>
                )}
                {isPravnaIliFizickaOsobaSelected && formik.values.person?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.person.address)}</span>
                    </Col>
                  </Row>
                )}

                {isPravnaIliFizickaOsobaSelected && formik.values.legalEntity?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.legalEntity.address)}</span>
                    </Col>
                  </Row>
                )}
                <AddressInput
                  field={formik.values.applicantAddress}
                  errors={formik.errors.applicantAddress}
                  readOnly={props.readOnly}
                  setFieldValue={(field: string, value: any) => {
                    formik.setFieldValue(`applicantAddress.${field}`, value).then(() => formik.validateForm());
                  }}
                />
                <Row>
                  <Col md={6}>
                    <Label labelText={'Email'} />
                    <Input
                      value={formik.values.email}
                      id={'email'}
                      name={'email'}
                      error={formik.errors.email ?? ''}
                      disabled={props.readOnly}
                      maxLength={60}
                      onChange={event => formik.setFieldValue(`email`, event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Telefon'} />
                    <Input
                      value={formik.values.telephoneNumber}
                      id={'telephoneNumber'}
                      name={'telephoneNumber'}
                      disabled={props.readOnly}
                      maxLength={50}
                      onChange={e => phoneChanged(e, 'telephoneNumber')}
                    />
                  </Col>
                </Row>
                {isPartySelectedEqual(APPLICANT_PARTY_TYPE_ENUM.OPUNOMOCENIK_PODNOSITELJA) && (
                  <>
                    <Row>
                      <Col md={12}>
                        <Label labelText={'Podnositelj zahtjeva'} />
                        <PaginatedDropdown
                          placeholder={!props?.readOnly && 'Odaberi podnositelje zahtjeva'}
                          onItemSelect={selected => formik.setFieldValue('applicants', selected)}
                          selected={formik.values.applicants}
                          labelKey={'value'}
                          options={applicantsOptionsLoading ? [] : applicantsOptions}
                          multiple
                          clearButton
                          disabled={props.readOnly}
                          error={(formik.errors?.applicants as string) ?? ''}
                          showErrorMessage
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Label labelText={'Vrsta punomoći'} />
                        <PaginatedDropdown
                          placeholder={!props?.readOnly && 'Odaberi vrstu punomoći'}
                          onItemSelect={selected => formik.setFieldValue('procurationType', selected)}
                          selected={formik.values.procurationType}
                          labelKey={'value'}
                          options={procurationTypeOptionsLoading ? [] : procurationTypeOptions}
                          disabled={props.readOnly || props.systemRecorded}
                          error={(formik.errors?.procurationType as string) ?? ''}
                          showErrorMessage
                        />
                      </Col>
                      <Col md={6}>
                        <Label labelText={'Punomoć aktivna'} />
                        <ToggleWithLabels
                          disabled={props.readOnly}
                          checked={formik.values.procurationStatus}
                          onChange={event => formik.setFieldValue('procurationStatus', event.target.checked)}
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </div>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.dirty || !formik.isValid}
                isLoading={isApplicantSaving}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CaseApplicantFormWithNonMandatoryOib;
